import React, { useState } from 'react';
import { X, Search, Radio, Package } from 'lucide-react';
import { useSiteStore } from '../../store/siteStore';
import { Equipment } from '../../types';
import { towerEquipmentDatabase } from '../../data/towerEquipment';

const EquipmentModal: React.FC = () => {
  const { isEquipmentModalOpen, setEquipmentModalOpen, selectedMountingPoint, addEquipmentToTower } = useSiteStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');

  if (!isEquipmentModalOpen || !selectedMountingPoint) return null;

  const filteredEquipment = towerEquipmentDatabase.filter(equipment => {
    const matchesSearch = equipment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         equipment.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || equipment.type === selectedType;
    return matchesSearch && matchesType;
  });

  const handleSelectEquipment = (equipment: Equipment) => {
    addEquipmentToTower(equipment, selectedMountingPoint.id);
    setEquipmentModalOpen(false);
  };

  const getIcon = () => {
    return <Radio size={16} className="text-blue-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] paper-preview-light">
      <div className="bg-white rounded-lg shadow-xl w-4/5 max-w-4xl h-4/5 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Add Equipment</h2>
            <p className="text-sm text-gray-600 mt-1">
              Mounting Point: {selectedMountingPoint.height}m, {selectedMountingPoint.side} side
            </p>
          </div>
          <button
            onClick={() => setEquipmentModalOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-md text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 placeholder:text-gray-400"
            />
          </div>

          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'radio-dish', label: 'Dishes' },
              { value: 'sector-antenna', label: 'Sectors' }
            ].map(type => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedType === type.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Equipment List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEquipment.map(equipment => (
              <div
                key={equipment.id}
                onClick={() => handleSelectEquipment(equipment)}
                className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-start space-x-3">
                  {getIcon()}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{equipment.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">
                      {equipment.manufacturer} • {equipment.type === 'radio-dish' ? 'Dish' : 'Sector Antenna'}
                    </p>
                    
                    {equipment.specifications && (
                      <div className="mt-2 text-xs text-gray-600 space-y-1">
                        {equipment.specifications.frequency && (
                          <div>Frequency: {equipment.specifications.frequency}</div>
                        )}
                        {equipment.specifications.gain && (
                          <div>Gain: {equipment.specifications.gain}</div>
                        )}
                        {equipment.specifications.beamwidth && (
                          <div>Beamwidth: {equipment.specifications.beamwidth}</div>
                        )}
                      </div>
                    )}
                    
                    {equipment.price && (
                      <div className="mt-2 text-sm font-medium text-green-600">
                        ${equipment.price}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredEquipment.length === 0 && (
            <div className="text-center py-12">
              <Package size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No equipment found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            Click on any equipment to add it to the mounting point. You can configure azimuth and elevation after adding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EquipmentModal;

